import { initializeApp, getApps } from 'firebase/app';
import {
    getAuth,
    GoogleAuthProvider,
    signInWithRedirect,
    getRedirectResult,
    signOut,
    onAuthStateChanged,
} from 'firebase/auth';

// Firebase / Google Identity Platform implementation of the client auth
// contract (see ./index.js).

const config = () => {
    const cfg = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    };
    const missing = Object.entries(cfg)
        .filter(([key, value]) => !value && key !== 'messagingSenderId')
        .map(([key]) => `REACT_APP_FIREBASE_${key.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`);
    if (missing.length) {
        throw new Error(`[auth] provider=firebase is missing config: ${missing.join(', ')}`);
    }
    return cfg;
};

// initializeApp throws if called twice with the same name, and CRA fast-refresh
// re-evaluates modules, so reuse any app that already exists.
const app = () => (getApps().length ? getApps()[0] : initializeApp(config()));
const auth = () => getAuth(app());

const toUser = (user) => (user
    ? {
        // `user_id` is the field the rest of the app reads; for Firebase it is
        // the UID, which is NOT the same value as the old Auth0 `sub`.
        user_id: user.uid,
        sub: user.uid,
        email: user.email,
        name: user.displayName,
        picture: user.photoURL,
        email_verified: user.emailVerified,
    }
    : null);

// Resolves once Firebase has restored any persisted session — the equivalent of
// the Auth0 provider's retry loop, but event-driven instead of timed.
const currentUser = () => new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth(), (user) => {
        unsubscribe();
        resolve(user);
    }, () => {
        unsubscribe();
        resolve(null);
    });
});

const createFirebaseAuthProvider = () => ({
    name: 'firebase',

    resetClient() {
        // Firebase keeps a single app instance; nothing to rebuild.
    },

    async getUser() {
        // Completes the redirect handshake if we just came back from Google.
        // Safe to call unconditionally; resolves null on a normal page load.
        try {
            await getRedirectResult(auth());
        } catch (_error) {
            // A failed/cancelled redirect must not prevent reporting an
            // existing session below.
        }
        return toUser(await currentUser());
    },

    async login(redirectPath = '') {
        if (redirectPath) {
            // Firebase returns to the page that started the flow, so remember
            // where the caller wanted to land and let the app route after boot.
            try {
                sessionStorage.setItem('AUTH_REDIRECT_PATH', redirectPath);
            } catch (_error) { /* private mode — fall back to the origin */ }
        }
        await signInWithRedirect(auth(), new GoogleAuthProvider());
    },

    async logout() {
        await signOut(auth());
    },

    async getAuthToken() {
        const user = await currentUser();
        if (!user) return null;
        // The API verifies this as a Firebase ID token; there is no separate
        // access token or audience to request, unlike Auth0.
        return user.getIdToken();
    },
});

export default createFirebaseAuthProvider;
